import { combineReducers } from '@reduxjs/toolkit';
import { connectRouter } from 'connected-react-router';
import { History } from 'history';

import { historicalScenariosApi } from './api/historical-scenarios/historical-scenarios-api';
import { planningPlanAccountsApi } from './api/planning/planning-api';
import { aiStatementReaderApi } from './api/ai-statement-reader';
import { screenerApi } from './api/screener';

import { Action } from '.';
import { ReduxState } from './modules/types';

import authReducer, { LOGOUT_SUCCESS } from './modules/auth';
import intlReducer from './modules/intl';
import fundsReducer from './modules/funds';
import altsReducer from './modules/alts';
import benchmarksReducer from './modules/benchmarks';
import stocksReducer from './modules/stocks';
import advancedSearchReducer from './modules/advanced-search';
import comparisonReducer from './modules/comparison';
import comparisonCorrelationReducer from './modules/comparison-correlations';
import portfoliosReducer from './modules/portfolios';
import quickSearchReducer, { QUICK_SEARCH_REDUCER_NAME } from './modules/quick-search';
import editPortfolioReducer from './modules/edit-portfolio';
import proposalV2Reducer from './modules/proposal-v2';
import contactsReducer from './modules/contacts';
import enterpriseReducer from './modules/enterprise';
import subscriptionReducer from './modules/subscription';
import comparisonBenchmarksReducer from './modules/comparison-benchmarks';
import editBenchmarkReducer from './modules/edit-benchmark';
import kycQuestionnaireReducer from './modules/kyc-questionnaire';
import QuestionnaireReducer from './modules/questionnaire';
import usersReducer from './modules/users';
import analyticsReducer from './modules/analytics';
import activityLogsReducer from './modules/activity-log';
import advisorsReducer from './modules/advisors';
import bmoGifReducer from './modules/bmo-gif';
import complianceAlternativesReducer from './modules/compliance-alternatives';
import rateLimitsReducer from './modules/rate-limits';
import taxesReducer from './modules/taxes';
import delegatesReducer from './modules/delegates';
import loadersReducer from './modules/ui/loaders';
import suitabilityAnalysisReducer from './modules/suitability-analysis';
import twoFactorAuthReducer from './modules/two-factor-auth';
import announcementsAdminReducer from './modules/announcements/admin';
import userAnnouncementsReducer from './modules/announcements/user';
import dataUploadConfigsReducer from './modules/enterprise/admin/data-upload';
import postMessageReducer from './modules/post-message';
import correlateRiskProfilesReducer from './modules/correlate-risk-profiles';
import omniRiskProfilesReducer from './modules/omni-risk-profiles';
import omniRiskProfileModalReducer from './modules/omni-risk-profile-modal';
import householdReducer from './modules/household';
import aiStatementReaderReducer from './modules/ai-statement-reader';
import aiStatementReaderMultiPortfolioReducer from './modules/ai-statement-reader-multi-portfolio';
import customProxiesReducer from './modules/custom-proxies';
// Utils
import { QUESTIONNAIRE_TYPES } from '../utils/questionnaire';
import { init as getIntlInitialState } from '../utils/i18n';

function createNamedWrapperReducer(reducerFunction: Function, reducerName: string): Function {
  return (state: ReduxState, action: Action) => {
    const { name } = action;

    const isInitializationCall = state === undefined;

    if (name !== reducerName && !isInitializationCall) {
      return state;
    }

    return reducerFunction(state, action);
  };
}

export default (history: History) => {
  const appReducer: (state: ReduxState | undefined, action: Action) => ReduxState = combineReducers({
    router: connectRouter(history),
    auth: authReducer,
    intl: intlReducer,
    funds: fundsReducer,
    alts: altsReducer,
    benchmarks: benchmarksReducer,
    stocks: stocksReducer,
    advancedSearch: advancedSearchReducer,
    comparison: comparisonReducer,
    comparisonCorrelations: comparisonCorrelationReducer,
    portfolios: portfoliosReducer,
    quickSearch: createNamedWrapperReducer(quickSearchReducer, QUICK_SEARCH_REDUCER_NAME.COMPARISON),
    quickSearchEditPortfolio: createNamedWrapperReducer(quickSearchReducer, QUICK_SEARCH_REDUCER_NAME.EDIT_PORTFOLIO),
    quickSearchEditBenchmark: createNamedWrapperReducer(quickSearchReducer, QUICK_SEARCH_REDUCER_NAME.EDIT_BENCHMARK),
    quickSearchEditBMOPortfolio: createNamedWrapperReducer(quickSearchReducer, QUICK_SEARCH_REDUCER_NAME.EDIT_BMO_PORTFOLIO),
    editPortfolio: editPortfolioReducer,
    proposalV2: proposalV2Reducer,
    contacts: contactsReducer,
    enterprise: enterpriseReducer,
    subscription: subscriptionReducer,
    comparisonBenchmarks: comparisonBenchmarksReducer,
    editBenchmarks: editBenchmarkReducer,
    kycQuestionnaire: kycQuestionnaireReducer,
    rationaleQuestionnaire: createNamedWrapperReducer(QuestionnaireReducer, QUESTIONNAIRE_TYPES.RATIONALE),
    users: usersReducer,
    analytics: analyticsReducer,
    activityLogs: activityLogsReducer,
    advisors: advisorsReducer,
    bmoGif: bmoGifReducer,
    complianceAlternatives: complianceAlternativesReducer,
    rateLimits: rateLimitsReducer,
    taxes: taxesReducer,
    delegates: delegatesReducer,
    ui: combineReducers({
      loaders: loadersReducer,
    }),
    suitabilityAnalysis: suitabilityAnalysisReducer,
    twoFactorAuth: twoFactorAuthReducer,
    announcementsAdmin: announcementsAdminReducer,
    userAnnouncements: userAnnouncementsReducer,
    dataUploadConfigs: dataUploadConfigsReducer,
    postMessage: postMessageReducer,
    aiStatementReader: aiStatementReaderReducer,
    aiStatementReaderMultiPortfolio: aiStatementReaderMultiPortfolioReducer,
    households: householdReducer,
    correlateRiskProfiles: correlateRiskProfilesReducer,
    omniRiskProfiles: omniRiskProfilesReducer,
    omniRiskProfileModal: omniRiskProfileModalReducer,
    customProxies: customProxiesReducer,
    // RTK Query API reducers
    [historicalScenariosApi.reducerPath]: historicalScenariosApi.reducer,
    [planningPlanAccountsApi.reducerPath]: planningPlanAccountsApi.reducer,
    [aiStatementReaderApi.reducerPath]: aiStatementReaderApi.reducer,
    [screenerApi.reducerPath]: screenerApi.reducer,
  });

  return (state: ReduxState | undefined, action: Action) => {
    if (action.type === LOGOUT_SUCCESS) {
      return appReducer({ intl: getIntlInitialState() } as ReduxState, action);
    }

    return appReducer(state, action);
  };
};
